/**
 * I5 / R10 — launch kit strings (fr = source; en/ar typed against it).
 * Wired under `i5.launchKit`.
 */
import type { Catalog } from './fr';

const en: Catalog = {
  title: 'Launch kit',
  subtitle:
    'Product Hunt, Show HN, directories, launch day, press, newsletter and posts — all prepared, you publish it yourself.',
  readOnly: 'Read only',
  readOnlyHint: 'Your role can view the kit but not change it.',
  loading: 'Loading the kit…',
  loadError: 'Could not load the launch kit.',
  manualNotice:
    'No automatic submission or posting: Karexia prepares the copy, you submit and publish yourself, and never solicit votes.',
  llmFallback:
    'The AI model was unavailable: this kit was produced by the deterministic generator from your information. You can regenerate it later.',
  grounding: {
    brief: 'Based on your product brief',
    user: 'Based on the information you entered',
    'brief+user': 'Based on your product brief and your information',
    none: 'No product source',
    voice: 'Brand voice applied',
  },
  form: {
    title: 'Generate a kit',
    newKit: 'New kit',
    hide: 'Hide',
    launchDate: 'Launch date',
    productName: 'Product name',
    productUrl: 'Product URL',
    oneLiner: 'One-liner (what the product does)',
    audience: 'Target audience',
    lang: 'Kit language',
    langFr: 'French',
    langEn: 'English',
    hint: 'Leave blank to reuse your product brief. No fact is invented.',
    submit: 'Generate the kit (3 credits)',
    submitting: 'Generating…',
    generated: 'Launch kit generated.',
    dateRequired: 'Pick a launch date.',
  },
  kitSelect: 'Kit',
  kitOption: '{{name}} — {{date}}',
  version: 'Version {{n}}',
  deleteKit: 'Delete kit',
  deleteConfirmTitle: 'Delete this kit?',
  deleteConfirm: 'The kit, its post drafts, its calendar reminders and its newsletter draft will be deleted.',
  deleteConfirmLabel: 'Delete',
  deleted: 'Kit deleted.',
  empty: 'No kit yet. Enter your launch date to generate one.',
  copy: 'Copy',
  copied: 'Copied to clipboard.',
  copyFailed: 'Copy failed — select the text manually.',
  save: 'Save',
  saving: 'Saving…',
  saved: 'Saved.',
  error: 'Something went wrong.',
  chars: '{{n}} / {{max}} characters',
  tabs: {
    productHunt: 'Product Hunt',
    hackerNews: 'Hacker News',
    directories: 'Directories',
    launchDay: 'Launch day',
    press: 'Press',
    newsletter: 'Newsletter',
    posts: 'Posts',
  },
  ph: {
    tagline: 'Product Hunt tagline',
    taglineHint: '60 characters max.',
    taglineTooLong: 'The tagline is longer than 60 characters.',
    variants: 'Variants',
    useVariant: 'Use',
    description: 'Description',
    maker: 'Maker first comment',
    gallery: 'Gallery shot list',
    openPh: 'Open Product Hunt (new tab)',
  },
  hn: {
    title: 'Show HN title',
    post: 'Post text',
    guidelines: 'Show HN rules',
    openGuidelines: 'Read the official rules',
    openHn: 'Open the HN submit page (new tab)',
  },
  dir: {
    intro:
      'Curated, versioned directory list (v{{v}}). Drag a card or use the buttons to track your manual submissions.',
    columns: { todo: 'To do', submitted: 'Submitted', live: 'Live', rejected: 'Rejected' },
    moveTo: 'Move to "{{status}}"',
    open: 'Open submission page',
    rules: 'Rules',
    details: 'Details',
    hideDetails: 'Hide',
    cost: { free: 'Free', freemium: 'Free / paid', paid: 'Paid', revenue_share: 'Revenue share' },
    liveUrl: 'Live listing URL',
    liveUrlRequired: 'Enter the live listing URL to mark it "Live".',
    markLive: 'Mark live',
    notes: 'Notes',
    saveNotes: 'Save notes',
    copyTitle: 'Copy tailored to this directory',
    genCopy: 'Generate tailored copy (1 credit)',
    genCopyRunning: 'Generating…',
    moved: '"{{name}}" → {{status}}',
    invalidMove: 'That move is not allowed from "{{from}}".',
    reset: 'Reset',
    submittedAt: 'Submitted on {{date}}',
    counts: '{{todo}} to do · {{submitted}} submitted · {{live}} live · {{rejected}} rejected',
    dragHint: 'Drag and drop',
  },
  day: {
    intro: 'Hour-by-hour plan from T-7 to T+1 (time zone: {{tz}}).',
    addToCalendar: 'Add to calendar',
    adding: 'Adding…',
    added: '{{n}} step(s) added to the calendar.',
    alreadyAdded: '{{n}} step(s) already in the calendar.',
    openCalendar: 'Open the calendar',
    minutes: '{{n}} min',
  },
  press: {
    pitch: 'Press pitch',
    subject: 'Subject',
    body: 'Message',
    criteria: 'Criteria to build your journalist list',
    criteriaNotice:
      'Criteria only: no names or contacts are provided or scraped. Find journalists yourself on the outlets’ official pages.',
    beats: 'Beats',
    outletTypes: 'Outlet types',
    angles: 'Angles',
    timing: 'Timing',
    do: 'Do',
    dont: 'Don’t',
  },
  news: {
    subject: 'Subject',
    preheader: 'Preheader',
    body: 'Message',
    recipients: 'Recipients (one email per line, e.g. your waitlist)',
    recipientsHint:
      'Every recipient goes through the CASL / Law 25 check: without recorded express consent, they are blocked.',
    createDraft: 'Create the draft and run the compliance check',
    creating: 'Creating…',
    draftCreated: 'Campaign draft created (not sent).',
    invalidEmail: 'Address refused by the email service: {{email}}. Fix or remove it, then try again.',
    preflight: 'CASL / Law 25 preflight',
    total: 'Recipients: {{n}}',
    allowed: 'Allowed: {{n}}',
    blocked: 'Blocked: {{n}}',
    reasons: 'Block reasons',
    unavailable: 'Compliance service unavailable — every recipient counts as blocked.',
    status: 'Campaign status: {{status}}',
    openEditor: 'Open the campaign editor',
    noSend: 'Nothing is sent from here. You send it yourself from the campaign editor, after the preflight.',
  },
  posts: {
    intro: 'Posts are created as drafts: nothing is scheduled or published automatically. You schedule each draft yourself, at the time you pick.',
    schedule: 'Schedule this draft',
    scheduleAt: 'Go-live date and time',
    scheduleFuture: 'Pick a future date.',
    scheduled: 'Draft scheduled for {{at}}.',
    scheduledFor: 'Scheduled for {{at}}',
    statusLabel: 'Status: {{status}}',
    draftGone: 'This draft was deleted from contents.',
    create: 'Create the drafts',
    creating: 'Creating…',
    created: '{{n}} draft(s) created.',
    draftOf: '{{platform}} draft',
    openDraft: 'Open the draft',
    openQueue: 'Open contents',
    platforms: { twitter: 'X (Twitter)', linkedin: 'LinkedIn', threads: 'Threads', bluesky: 'Bluesky' },
  },
};

export default en;
